import { useCallback, useMemo, useState } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import { faClipboard } from "@fortawesome/free-regular-svg-icons/faClipboard";
import { faThumbsDown } from "@fortawesome/free-regular-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons/faThumbsUp";
import { faThumbsDown as faThumbsDownSolid } from "@fortawesome/free-solid-svg-icons/faThumbsDown";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/free-solid-svg-icons/faThumbsUp";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import RichContent from "components/RichContent";
import ShareButton from "components/ShareButton";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";

const InsightSmartTakes = ({ insight }: { insight: Insight }) => {
  const { account: loggedInAccount } = useAuthentication();
  const { trackActivity } = useActivityTracker();
  const { setNotification } = useNotification();

  const [smartSummaryLiked, setSmartSummaryLiked] = useState(false);
  const [smartSummaryDisliked, setSmartSummaryDisliked] = useState(false);

  const { publicationType, tagline } = insight;

  const { bullishPoints, bearishPoints, catalysts, keySummaryPoints } =
    insight?.smartSummary || {};

  const smartTakesCopy = useCallback(
    (
      newLine = "\n",
      shouldEncodeURI = false,
      shouldIncludeLink = true,
      linkToHtml = false
    ) => {
      let text = `✨ Smart Takes from ${
        shouldEncodeURI ? encodeURIComponent(tagline) : tagline
      }${newLine}${newLine}`;

      text += `Key Takeaways${newLine}`;
      keySummaryPoints?.forEach((c) => {
        text += `- ${shouldEncodeURI ? encodeURIComponent(c) : c} ${newLine}`;
      });

      text += `${newLine}Bullish Points ${newLine}`;
      bullishPoints?.forEach((c) => {
        text += `- ${shouldEncodeURI ? encodeURIComponent(c) : c} ${newLine}`;
      });

      text += `${newLine}Bearish Points${newLine}`;
      bearishPoints?.forEach((c) => {
        text += `- ${shouldEncodeURI ? encodeURIComponent(c) : c} ${newLine}`;
      });

      text += `${newLine}Catalysts${newLine}`;
      catalysts?.forEach((c) => {
        text += `- ${shouldEncodeURI ? encodeURIComponent(c) : c} ${newLine}`;
      });

      if (shouldIncludeLink) {
        let link = shouldEncodeURI
          ? encodeURIComponent(insight.shareableLinks.direct)
          : insight.shareableLinks.direct;

        if (linkToHtml) {
          link = `<a href="${link}">${link}</a>`;
        }

        text += `${newLine}${newLine}${link}`;
      }

      return text;
    },
    [
      bearishPoints,
      bullishPoints,
      catalysts,
      insight.shareableLinks,
      keySummaryPoints,
      tagline,
    ]
  );

  const onCopySmartTakes = useCallback(() => {
    navigator.clipboard.writeText(smartTakesCopy("\n"));
    setNotification("Copied to clipboard successfully.", "success");
  }, [setNotification, smartTakesCopy]);

  const shareSmartTakes = useMemo(() => {
    return {
      email: {
        link: `mailto:?subject=✪ Smartkarma | ${
          publicationType || "Insights"
        }: | ${encodeURIComponent(tagline)}&body=${smartTakesCopy(
          "%0A",
          true
        )}`,
      },
      clipboard: {
        message: smartTakesCopy("\n"),
      },
      privateMessage: {
        message: smartTakesCopy("<br>", false, true, true),
      },
      whatsapp: {
        link: `https://web.whatsapp.com/send?text=${smartTakesCopy(
          "%0A",
          true
        )}`,
      },
      twitter: {
        link: `https://twitter.com/share?url=${
          insight.shareableLinks.direct
        }&text=${encodeURIComponent(tagline)}&via=smartkarma`,
      },
      linkedIn: {
        link: `https://www.linkedin.com/shareArticle?mini=true&url=${insight.shareableLinks.direct}&source=Smartkarma`,
      },
    };
  }, [insight.shareableLinks.direct, publicationType, smartTakesCopy, tagline]);

  return (
    <>
      <RichContent className="p-[15px] pt-0 font-light mt-[15px] text-subtle-black rounded-lg">
        <div className="flex justify-end mb-[-30px] mr-[-30px]">
          <OverlayTrigger
            placement={"bottom"}
            overlay={<Tooltip>Copy</Tooltip>}
          >
            <button
              className="!outline-0 !border-0 bg-transparent m-[10px] mx-[20px] mr-[0px] text-intelligent-silver hover:!text-shady-gray"
              onClick={onCopySmartTakes}
            >
              <FontAwesomeIcon icon={faClipboard} className="text-[18px]" />
            </button>
          </OverlayTrigger>

          <ShareButton
            shareKey={`share-smart-takes-${insight.id}`}
            className="flex !text-[18px] justify-center items-center m-[10px] text-intelligent-silver hover:!text-shady-gray mx-[20px] ml-[10px]"
            activeClassName="flex justify-center items-center text-shady-gray m-[10px] mx-[20px] ml-[10px]"
            options={shareSmartTakes}
            iconSize="18px"
            tooltipPlacement="bottom"
            columnView
            noBorder
          ></ShareButton>
        </div>
        <div className="mt-[15px] font-roboto font-[400]">
          {!!keySummaryPoints?.length && (
            <div>
              <h5 className="font-[600]">Key Takeaways</h5>
              <ul>
                {keySummaryPoints.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {!!bullishPoints?.length && (
            <div>
              <h5 className="font-[600]">Bullish Points</h5>
              <ul>
                {bullishPoints.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {!!bearishPoints?.length && (
            <div>
              <h5 className="font-[600]">Bearish Points</h5>
              <ul>
                {bearishPoints.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {!!catalysts?.length && (
            <div>
              <h5 className="font-[600]">Catalysts Watch</h5>
              <ul>
                {catalysts.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {/* {!!relatedThemes?.length && (
          <div>
            <h5 className="font-[600]">Related Topics</h5>
            <div className="flex flex-wrap gap-[10px] mt-[5px]">
              {relatedThemes.map((t) => (
                <div
                  key={t}
                  className="text-[15px] text-white bg-[rgba(13,69,103,.8)] rounded-[7px] px-[10px]"
                >
                  {t}
                </div>
              ))}
            </div>
          </div>
        )} */}
        </div>
      </RichContent>

      <div className="flex">
        <div className="flex-1"></div>
        <div
          className={twMerge(
            "flex space-x-[15px] text-[14px]",
            "bg-[rgba(0,0,0,.5)] text-white px-[10px] py-[5px] rounded"
          )}
        >
          <div>
            {loggedInAccount?.isInsightProvider &&
            ["external", "trial"].includes(loggedInAccount?.insightProviderType)
              ? "Is this accurate?"
              : "Is this helpful?"}
          </div>
          <div
            className={twMerge("cursor-pointer", "text-gray-gray")}
            onClick={() => {
              trackActivity("smart_takes.liked", insight, {
                insightId: insight.id,
              });
              if (smartSummaryLiked) {
                setSmartSummaryLiked(false);
              } else {
                setSmartSummaryLiked(true);
                setSmartSummaryDisliked(false);
              }
            }}
          >
            <FontAwesomeIcon
              className="text-white"
              icon={smartSummaryLiked ? faThumbsUpSolid : faThumbsUp}
            />
          </div>
          <div
            className={twMerge("cursor-pointer", "text-gray-gray")}
            onClick={() => {
              trackActivity("smart_takes.disliked", insight, {
                insightId: insight.id,
              });
              if (smartSummaryDisliked) {
                setSmartSummaryDisliked(false);
              } else {
                setSmartSummaryLiked(false);
                setSmartSummaryDisliked(true);
              }
            }}
          >
            <FontAwesomeIcon
              className="text-white"
              icon={smartSummaryDisliked ? faThumbsDownSolid : faThumbsDown}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default InsightSmartTakes;
